import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Plus } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useDismissable } from '../../lib/useDismissable';
import { useProxyImage } from '../../lib/useProxyImage';
import { useTenant } from '../../auth/TenantContext';
import { MenuScrim, chromeFocusRing, chromeMenuPanel, chromeMenuRowTouch } from './MenuItem';
import { menuIcons } from './menuIcons';
import { useShellMenu } from './useShellMenu';
import type { Tenant } from '@mg/shared';

/**
 * A workspace's icon: its brand icon logo, or its initial while that loads or when it has none. Each
 * workspace's logo is fetched AS that workspace - asset routes are workspace-scoped, so the active
 * workspace's credentials would not reach another's logo.
 *
 * A 22px tile with a 5px radius at full size. A real logo sits on white, so a transparent one stays
 * legible on the dark chrome; the initial sits on graphite.
 */
export function WorkspaceIcon({ tenant }: { tenant: Pick<Tenant, 'slug' | 'name' | 'brandAssets'> }) {
  const iconUrl = useProxyImage(tenant.brandAssets?.logos?.icon ?? null, tenant.slug);
  return (
    <span
      className={cn(
        'flex h-[1.375rem] w-[1.375rem] shrink-0 items-center justify-center overflow-hidden rounded-[0.3125rem] text-[0.6875rem] font-bold',
        iconUrl ? 'bg-white' : 'bg-[#303036] text-chrome-text',
      )}
      aria-hidden
    >
      {iconUrl ? (
        <img src={iconUrl} alt="" className="h-full w-full rounded-[0.3125rem] object-cover" />
      ) : (
        tenant.name.charAt(0).toUpperCase()
      )}
    </span>
  );
}

/**
 * Top-bar control for the active Brand Workspace, right of the logo divider. Shows the active
 * workspace's icon and name and a dropdown to switch between workspaces, add a new one, or edit the
 * active one. When the user has no workspace yet, it shows a "Create a Brand Workspace" affordance
 * instead.
 *
 * From `lg` up the picker sits at the LEFT of the bar, so its menu opens from its left edge (a
 * right-anchored menu would open back over the logo). Below `lg` it sits at the right, beside the
 * account menu, and the bar anchors the menu instead, under the bar: 12px from the left edge on a phone
 * (340px wide, never wider than the screen less 24px), 16px from the right edge on a tablet.
 */
export function WorkspaceSwitcher() {
  const { tenants, activeTenant, hasWorkspace, isLoading, setActiveTenant } = useTenant();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);
  useDismissable(open, ref, close);
  useShellMenu(open, close, ref);

  if (isLoading) {
    return (
      <div
        className="h-9 w-44 animate-pulse max-md:w-24 rounded-lg border border-chrome-control-line bg-chrome-control"
        aria-hidden
      />
    );
  }

  // No workspace yet: prompt creation directly (the gate also forces this).
  if (!hasWorkspace || !activeTenant) {
    return (
      <button
        type="button"
        onClick={() => navigate('/workspace/new')}
        className={cn(
          'flex h-9 min-w-0 max-w-full items-center gap-2 rounded-lg border border-dashed border-white/25 bg-chrome-control px-3 text-[0.8125rem] font-medium text-chrome-text transition-colors hover:bg-chrome-hover max-lg:min-h-[var(--touch-target)]',
          chromeFocusRing,
        )}
      >
        <Plus className="h-4 w-4 shrink-0" aria-hidden />
        <span className="truncate">Create a Brand Workspace</span>
      </button>
    );
  }

  return (
    <>
      <div ref={ref} className="min-w-0 lg:relative">
        <button
          type="button"
          className={cn(
            'flex h-9 max-w-[18rem] items-center gap-2 rounded-lg border border-chrome-control-line bg-chrome-control pl-2 pr-2.5 text-[0.8125rem] font-medium text-chrome-text transition-colors hover:bg-chrome-hover max-lg:max-w-full',
            // Below `lg`: a 44px target, outlined while its menu is open.
            'max-lg:min-h-[var(--touch-target)] max-lg:aria-expanded:border-white/40',
            chromeFocusRing,
          )}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="flex min-w-0 items-center gap-2">
            <WorkspaceIcon tenant={activeTenant} />
            <span className="truncate">{activeTenant.name}</span>
          </span>
          <ChevronDown
            className={cn('h-3.5 w-3.5 shrink-0 text-chrome-muted transition-transform', open && 'rotate-180')}
            aria-hidden
          />
        </button>

        {open && (
          <div
            role="menu"
            aria-label="Brand Workspaces"
            className={cn(
              'absolute z-20 overflow-hidden py-1 lg:left-0 lg:mt-1 lg:w-64',
              'max-lg:top-full max-lg:mt-2 max-lg:w-[21.25rem] max-lg:max-w-[calc(100vw-1.5rem)] max-md:left-3 md:max-lg:right-4',
              chromeMenuPanel,
            )}
          >
            <p className="px-3 pb-1 pt-2 text-[0.6875rem] font-semibold uppercase tracking-wide text-chrome-muted">
              Brand Workspaces
            </p>
            <ul role="none">
              {tenants.map((t) => {
                const active = t.id === activeTenant.id;
                return (
                  <li key={t.id} role="none">
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={active}
                      onClick={() => {
                        setActiveTenant(t.slug);
                        setOpen(false);
                      }}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-chrome-text transition-colors hover:bg-chrome-hover',
                        active && 'bg-chrome-hover',
                        chromeMenuRowTouch,
                        chromeFocusRing,
                        'focus-visible:ring-inset',
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <WorkspaceIcon tenant={t} />
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-chrome-text">{t.name}</span>
                          {t.isDemo && (
                            <span className="block truncate text-xs text-chrome-muted">Demo</span>
                          )}
                        </span>
                      </span>
                      {active && (
                        <span className="text-chrome-text" aria-hidden>
                          ✓
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="my-1 border-t border-chrome-control-line" />

            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate('/workspace/edit');
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-chrome-text transition-colors hover:bg-chrome-hover',
                chromeMenuRowTouch,
                chromeFocusRing,
                'focus-visible:ring-inset',
              )}
            >
              <menuIcons.workspaceEdit className="h-4 w-4 text-chrome-muted" aria-hidden />
              Edit Brand Workspace
            </button>

            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate('/workspace/new');
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-chrome-text transition-colors hover:bg-chrome-hover',
                chromeMenuRowTouch,
                chromeFocusRing,
                'focus-visible:ring-inset',
              )}
            >
              <menuIcons.workspaceCreate className="h-4 w-4 text-chrome-muted" aria-hidden />
              Add New Brand Workspace
            </button>
          </div>
        )}
        {/* Fixed, under the bar: its place among the wrapper's children does not matter. */}
        {open && <MenuScrim onDismiss={close} className="z-10" />}
      </div>
    </>
  );
}
